
import React, { useState, useEffect } from 'react';
import Typography from '../../components/Typography/Typography';
import Button from '../../components/Button/Button';
import AddWhitelistUser from '../../dialogs/AddWhitelistUser/AddWhitelistUser';
import DeleteWhitelistUser from '../../dialogs/DeleteWhitelistUser/DeleteWhitelistUser';
import Pagination from '../../components/Pagination/Pagination';
import DataTable, { dataTableRowClass, dataTableTdClass, dataTableThClass, searchInputClass, TABLE_HEIGHT } from '../../components/DataTable/DataTable';
import { FiUsers } from 'react-icons/fi';
import { ClipLoader } from 'react-spinners';
import ASSETS from '../../assets';
import { authAPI, WhitelistedUser, WhitelistedUsersResponse, SearchWhitelistedUsersResponse } from '../../services/api';

const WhiteList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState<WhitelistedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  const itemsPerPage = 10;

  // Fetch whitelisted users
  const fetchUsers = async (page: number = currentPage, search: string = '') => {
    try {
      setLoading(true);
      setError(null);
      
      if (search && search.trim()) {
        // Search API call
        const searchResponse: SearchWhitelistedUsersResponse = await authAPI.searchWhitelistUser({ input: search.trim() });
        
        setUsers(searchResponse.results);
        // For search results, create a mock pagination object
        setPagination({
          total: searchResponse.count,
          page: 1,
          limit: searchResponse.count,
          totalPages: 1
        });
      } else {
        // Regular pagination API call
        const response: WhitelistedUsersResponse = await authAPI.getWhitelistedUsers({ 
          page, 
          limit: itemsPerPage 
        });
        
        setUsers(response.users);
        setPagination(response.pagination);
      }
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Failed to fetch whitelisted users';
      setError(errorMessage);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Initial load and page changes
  useEffect(() => {
    fetchUsers(currentPage, searchTerm);
  }, [currentPage]);

  // Search with debounce
  useEffect(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      fetchUsers(1, searchTerm);
    }, 500);

    setSearchTimeout(timeout);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleAddUserSuccess = () => {
    fetchUsers(currentPage, searchTerm);
  };

  const handleDeleteUser = (email: string) => {
    setSelectedUserEmail(email);
    setIsDeleteUserModalOpen(true);
  };

  const handleDeleteUserSuccess = () => {
    fetchUsers(currentPage, searchTerm);
  };

  



  return (
    <div className='flex-grow bg-white  rounded-[15px] flex flex-col'>


      <div className='  rounded-lg'>
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div className="flex items-center">

            <Typography variant="h2" className="font-bold title text-xl sm:text-2xl lg:text-2xl">
              DB whitelisting Users
            </Typography>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Search Bar */}
            <div className="relative flex-1 sm:flex-none sm:w-80">
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={searchInputClass}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-2">
                {/* Clear Button */}
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200"
                    title="Clear search"
                  >
                    <img src={ASSETS.cross} alt="Clear" className="w-3 h-3 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
                {/* Search Icon */}
                {ASSETS.search && (
                  <img src={ASSETS.search} alt="Search" className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>

            {/* Add User Button */}
            <Button
              label="Add User"
              variant="primary"
              onClick={() => setIsAddUserModalOpen(true)}
              className="w-full sm:w-auto flex items-center gap-1 !h-[38px]"
              leftIcon={ASSETS.add}
            />
          </div>
        </div>

        {/* Table Container */}
        <DataTable
          footer={
            !loading && !error && users?.length > 0 ? (
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={handlePageChange}
                totalItems={pagination.total}
                itemsPerPage={itemsPerPage}
                embedded
              />
            ) : undefined
          }
        >
            <table className="w-full border-collapse">
              <thead className="sticky top-0 z-10 shadow-[0_1px_0_0_rgba(226,232,240,1)]">
                <tr>
                  <th className={`${dataTableThClass} text-center min-w-[40px]`}>
                    Sr.
                  </th>
                  <th className={`${dataTableThClass} text-left min-w-[250px]`}>
                    Email
                  </th>
                  <th className={`${dataTableThClass} text-center min-w-[130px]`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={3} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <ClipLoader size={40} color="#3B82F6" />
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={3} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-red-100 rounded-full">
                          <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <Typography variant="h3" className="text-red-500 font-medium">
                          Error loading users
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {error}
                        </Typography>
                        <Button
                          label="Retry"
                          variant="outlined"
                          onClick={() => fetchUsers(currentPage, searchTerm)}
                          className="mt-2"
                        />
                      </div>
                    </td>
                  </tr>
                ) : users?.length > 0 ? (
                  users?.map((user, index) => (
                    <tr key={user.email} className={dataTableRowClass}>
                      <td className={`${dataTableTdClass} text-center min-w-[40px] font-medium text-slate-500`}>
                        {(pagination.page - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className={`${dataTableTdClass} min-w-[250px]`}>
                        {user.email}
                      </td>
                      <td className={`${dataTableTdClass} text-center min-w-[130px]`}>
                        <div className="flex justify-center gap-6">
                          <button
                            onClick={() => handleDeleteUser(user.email)}
                            className="p-1 hover:bg-red-50 rounded-full transition-colors duration-200"
                            title="Remove user"
                          >
                            <img src={ASSETS.delete} alt="delete" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="text-center align-middle" style={{ height: TABLE_HEIGHT - 48 }}>
                      <div className="flex flex-col items-center justify-center gap-3 h-full">
                        <div className="p-3 bg-gray-100 rounded-full">
                          {FiUsers({ className: "w-8 h-8 text-gray-400" })}
                        </div>
                        <Typography variant="h3" className="text-gray-500 font-medium">
                          {searchTerm ? 'No users found' : 'No users available'}
                        </Typography>
                        <Typography variant="body1" className="text-gray-400">
                          {searchTerm
                            ? `No users match "${searchTerm}". Try adjusting your search terms.`
                            : 'No users have been added yet. Click "Add User" to get started.'
                          }
                        </Typography>
                        {searchTerm && (
                          <button
                            onClick={handleClearSearch}
                            className="mt-2 px-4 py-2 text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200"
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
        </DataTable>
      </div>

      {/* Add User Modal */}
      <AddWhitelistUser
        open={isAddUserModalOpen}
        setOpen={setIsAddUserModalOpen}
        onSuccess={handleAddUserSuccess}
      />

      {/* Delete User Modal */}
      <DeleteWhitelistUser
        open={isDeleteUserModalOpen}
        setOpen={setIsDeleteUserModalOpen}
        userEmail={selectedUserEmail}
        onSuccess={handleDeleteUserSuccess}
      />
    </div>
  );
};

export default WhiteList;
